// File: RichTextEditor.js
import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Button from "components/Button";
import "./editor.css";

const MenuBar = ({ editor, uploadEndpoint, showImageButton }) => {
  if (!editor) return null;

  const setHeading = (level) => {
    const { from, to } = editor.state.selection;
    if (from === to) {
      editor.chain().focus().toggleHeading({ level }).run();
    } else {
      const selectedText = editor.state.doc.textBetween(from, to);
      const doc = editor.state.doc;
      const $from = doc.resolve(from);
      const $to = doc.resolve(to);
      if ($from.sameParent($to)) {
        editor.chain()
          .focus()
          .setTextSelection({ from: $from.start(), to: $from.end() })
          .toggleHeading({ level })
          .run();
      } else {
        editor.chain().focus().toggleHeading({ level }).run();
      }
      editor.commands.setTextSelection({ from, to });
    }
  };

  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();

  const setParagraph = () => {
    const { from, to } = editor.state.selection;
    if (from === to) {
      editor.chain().focus().setParagraph().run();
    } else {
      const doc = editor.state.doc;
      const $from = doc.resolve(from);
      const $to = doc.resolve(to);
      if ($from.sameParent($to)) {
        editor.chain()
          .focus()
          .setTextSelection({ from: $from.start(), to: $from.end() })
          .setParagraph()
          .run();
      } else {
        editor.chain().focus().setParagraph().run();
      }
      editor.commands.setTextSelection({ from, to });
    }
  };

  const addLineBreak = () => editor.chain().focus().setHardBreak().run();

  const addImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const tempUrl = URL.createObjectURL(file);
      const position = editor.state.selection.anchor;
      editor.chain()
        .focus()
        .setImage({ src: tempUrl, alt: file.name })
        .setTextSelection(position + 1)
        .run();
    };
    input.click();
  };

  const headingButton = (level) => (
    <Button
      type="button"
      onClick={() => setHeading(level)}
      title={`Heading ${level}`}
      className={
        editor.isActive("heading", { level }) ? "bg-blue-100 border-blue-300" : ""
      }
    >
      H{level}
    </Button>
  );

  return (
    <div className="flex gap-2 border rounded p-3 mb-2 bg-gray-50 flex-wrap items-center">
      <Button
        type="button"
        onClick={toggleBold}
        title="Bold"
        className={editor.isActive("bold") ? "bg-blue-100 border-blue-300" : ""}
      >
        <strong>B</strong>
      </Button>

      <Button
        type="button"
        onClick={toggleItalic}
        title="Italic"
        className={editor.isActive("italic") ? "bg-blue-100 border-blue-300" : ""}
      >
        <em>I</em>
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <Button
        type="button"
        onClick={setParagraph}
        title="Normal Text"
        className={editor.isActive("paragraph") ? "bg-blue-100 border-blue-300" : ""}
      >
        P
      </Button>

      {headingButton(1)}
      {headingButton(2)}
      {headingButton(3)}
      {headingButton(4)}

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <Button
        type="button"
        onClick={addLineBreak}
        title="Add Line Break"
        className="text-green-600"
      >
        Enter
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet List"
        className={editor.isActive("bulletList") ? "bg-blue-100 border-blue-300" : ""}
      >
        • List
      </Button>

      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered List"
        className={editor.isActive("orderedList") ? "bg-blue-100 border-blue-300" : ""}
      >
        1. List
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      {/* Conditionally render Image button */}
      {showImageButton && (
        <Button
          type="button"
          onClick={addImage}
          title="Add Image"
          className="text-purple-600"
        >
          Image
        </Button>
      )}
    </div>
  );
};

const RichTextEditor = ({
  value,
  onChange,
  uploadEndpoint,
  showImageButton = true, // default: show Image button
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <div className="rich-text-editor-container w-full">
      <MenuBar
        editor={editor}
        uploadEndpoint={uploadEndpoint}
        showImageButton={showImageButton}
      />
      <div className="border rounded p-4 min-h-[200px] max-h-[300px] overflow-y-auto bg-white">
        <EditorContent editor={editor} className="w-full h-full" />
      </div>
      <div className="text-xs text-gray-500 mt-1">
        <strong>How to format your article:</strong>
        <br />
        1. Select the text you want to format
        <br />
        2. Click the desired format (H1, H2, H3, H4, Bold, Italic)
        <br />
        3. Each paragraph can have different formatting
        <br />
        4. Use "Enter" button to add line breaks within paragraphs
        <br />
        {showImageButton && "5. Click \"Image\" to upload and insert images"}
      </div>
    </div>
  );
};

export default RichTextEditor;